import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { eventsService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const JudgeEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Helper function to safely format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      return date.toLocaleDateString();
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid date';
    }
  };
  const { user } = useAuth();

  useEffect(() => {
    console.log('🔍 JudgeEvents useEffect triggered');
    
    let isMounted = true;
    
    const loadEvents = async () => {
      try {
        console.log('🔍 Starting to load events, isMounted:', isMounted);
        if (!isMounted) return;
        setLoading(true);
        
        // Only get closed events - judges can only judge events that are closed
        console.log('🔍 Calling eventsService.getClosedEvents()');
        const closedResponse = await eventsService.getClosedEvents();
        console.log('🔍 Got response:', closedResponse);
        
        if (!isMounted) {
          console.log('🔍 Component unmounted, skipping state updates');
          return;
        }
        
        const closedEvents = closedResponse.data?.data?.events || closedResponse.data?.events || [];
        console.log('🔍 Parsed events:', closedEvents.length, 'events');
        
        // Sort events by end_date descending (most recently closed first)
        const sortedEvents = closedEvents.sort((a, b) => {
          const dateA = new Date(a.end_date);
          const dateB = new Date(b.end_date);
          return dateB - dateA; // Descending order
        });
        
        setEvents(sortedEvents);
        setError(''); // Clear any previous errors
        setLoading(false);
      } catch (err) {
        console.error('🔍 Error loading events:', err);
        if (!isMounted) {
          console.log('🔍 Component unmounted, skipping error state updates');
          return;
        }
        setError('Failed to load events available for judging');
        setEvents([]); // Clear events on error
        setLoading(false);
      }
    };
    
    loadEvents();
    
    // Cleanup function
    return () => {
      console.log('🔍 JudgeEvents cleanup');
      isMounted = false;
    };
  }, []);

  const fetchEvents = async () => {
    // Only get closed events - judges can only judge events that are closed
    const closedResponse = await eventsService.getClosedEvents();
    
    const closedEvents = closedResponse.data?.data?.events || closedResponse.data?.events || [];
    
    // Sort events by end_date descending (most recently closed first)
    const sortedEvents = closedEvents.sort((a, b) => {
      const dateA = new Date(a.end_date);
      const dateB = new Date(b.end_date);
      return dateB - dateA; // Descending order
    });
    
    setEvents(sortedEvents);
  };

  const handleJudgeEvent = (eventId) => {
    navigate(`/judge/events/${eventId}`);
  };

  const getEventStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'closed':
        return 'warning';
      case 'pending':
        return 'info';
      default:
        return 'default';
    }
  };

  if (!user?.role || !['judge', 'admin'].includes(user.role)) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          You don't have permission to access this page. Only judges and admins can judge events.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading events...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Judge Events
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Review and judge stories from closed events. Only events that have ended are available for judging to ensure fair evaluation of all submissions.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {events.length === 0 ? (
        <Alert severity="info">
          No closed events available for judging at the moment. Events must be closed before judging can begin.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {events.map((event) => {
            const hasSubmissions = (event.submission_count || 0) > 0;
            const judgingProgress = hasSubmissions ? (event.judged_stories_count || 0) / event.submission_count : 0;
            const isFullyJudged = hasSubmissions && judgingProgress === 1;
            
            return (
              <Grid item xs={12} md={6} lg={4} key={event.event_id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    position: 'relative',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    },
                    border: isFullyJudged ? '2px solid' : '1px solid',
                    borderColor: isFullyJudged ? 'success.main' : 'divider'
                  }}
                >
                  {/* Header with title and status */}
                  <Box sx={{ 
                    background: `linear-gradient(135deg, ${isFullyJudged ? '#e8f5e8' : '#f8f9fa'} 0%, ${isFullyJudged ? '#c8e6c9' : '#e3f2fd'} 100%)`,
                    p: 3,
                    pb: 2
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" component="h2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {event.title}
                      </Typography>
                      <Chip
                        label={isFullyJudged ? 'Complete' : event.status}
                        color={isFullyJudged ? 'success' : getEventStatusColor(event.status)}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </Box>
                    
                    {event.theme && (
                      <Chip 
                        label={`📚 ${event.theme}`}
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1, fontWeight: 500 }}
                      />
                    )}
                  </Box>

                  <CardContent sx={{ flexGrow: 1, pt: 2 }}>
                    <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 3 }}>
                      {event.description}
                    </Typography>

                    {/* Progress Section */}
                    {hasSubmissions && (
                      <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Judging Progress
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {event.judged_stories_count || 0} / {event.submission_count} stories
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          width: '100%', 
                          height: 8, 
                          bgcolor: 'grey.200', 
                          borderRadius: 1,
                          overflow: 'hidden'
                        }}>
                          <Box sx={{ 
                            width: `${judgingProgress * 100}%`, 
                            height: '100%', 
                            bgcolor: isFullyJudged ? 'success.main' : 'primary.main',
                            transition: 'width 0.3s ease-in-out'
                          }} />
                        </Box>
                      </Box>
                    )}

                    {/* Event Details Grid */}
                    <Box sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr 1fr', 
                      gap: 2, 
                      mb: 2,
                      '& > div': {
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        p: 1.5,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider'
                      }
                    }}>
                      <Box>
                        <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600 }}>
                          {event.submission_count || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Submissions
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h6" color="secondary.main" sx={{ fontWeight: 600 }}>
                          {event.participant_count || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Participants
                        </Typography>
                      </Box>
                    </Box>

                    {/* Event Timeline */}
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" display="block" sx={{ fontWeight: 600, mb: 1 }}>
                        📅 Event Timeline
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {formatDate(event.start_date)} → {formatDate(event.end_date)}
                      </Typography>
                      {event.word_limit && (
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                          📝 Max {event.word_limit} words
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                  
                  {/* Action Button */}
                  <Box sx={{ p: 3, pt: 0 }}>
                    <Button
                      variant={hasSubmissions ? "contained" : "outlined"}
                      fullWidth
                      size="large"
                      onClick={() => handleJudgeEvent(event.event_id)}
                      disabled={event.status === 'pending' || !hasSubmissions}
                      sx={{ 
                        py: 1.5,
                        fontWeight: 600,
                        ...(isFullyJudged && {
                          bgcolor: 'success.main',
                          '&:hover': { bgcolor: 'success.dark' }
                        })
                      }}
                      startIcon={
                        event.status === 'pending' ? '⏳' :
                        !hasSubmissions ? '📭' :
                        isFullyJudged ? '✅' : '⚖️'
                      }
                    >
                      {event.status === 'pending' 
                        ? 'Not Yet Active' 
                        : !hasSubmissions 
                          ? 'No Submissions to Judge'
                          : isFullyJudged
                            ? 'Review Completed Judging'
                            : 'Judge Stories'
                      }
                    </Button>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Container>
  );
};

export default JudgeEvents;
